import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography
} from '@mui/material';
import dayjs from 'dayjs';
import { useCallback, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { PRODUCTS } from '@/constants/products.constants';

import { useProductDialog } from './ProductEditDialog.hooks';
import type {
  ProductCreateRequestDTO,
  ProductEditDialogForm
} from './ProductEditDialog.types';
import { ProductEditDialogContentEdit } from './ProductEditDialogContentEdit';

export const ProductEditDialog = () => {
  const { error, mutation, open, product, mode, closeDialog } =
    useProductDialog();

  const productClone = useMemo(
    () => ({
      ...product,
      start_date: product.start_date ? dayjs(product.start_date) : null,
      end_date: product.end_date ? dayjs(product.end_date) : null,
      product_type: product.product_type || PRODUCTS.LOG,
      subscription_limit: +product.subscription_limit,
      subscription_unit: product.subscription_unit || 'Endpoints',
      retention: Number(product.retention || 0).toString(),
      archive_retention: Number(product.archive_retention || 0).toString(),
      sub_type: product.sub_type || ''
    }),
    [product]
  );

  const methods = useForm<ProductEditDialogForm>({
    defaultValues: productClone,
    mode: 'onChange'
  });

  const onSubmit = useCallback(
    (data: ProductEditDialogForm) => {
      const productData = {
        ...product,
        ...data,
        start_date: data.start_date!.toISOString(),
        end_date: data.end_date!.toISOString()
      };
      mutation.mutate({
        data: productData as ProductCreateRequestDTO,
        id: productData.id
      });
    },
    [product, mutation]
  );

  const title = useMemo(() => {
    let titleStr = `Add New Product`;
    if (mode === 'Delete') {
      titleStr = 'Remove Product';
    }
    if (mode === 'Edit') {
      titleStr = `Edit Product (ID ${product.id})`;
    }
    return titleStr;
  }, [mode, product.id]);

  return (
    <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            {mode !== 'Delete' && <ProductEditDialogContentEdit />}
            {mode === 'Delete' && (
              <Typography variant="body1" sx={{ my: 2 }}>
                Are you sure you want to delete the product{' '}
                <strong>{product.code}?</strong>
              </Typography>
            )}

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </form>
        </FormProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDialog}>Cancel</Button>
        <Button
          variant="contained"
          color={mode === 'Delete' ? 'error' : 'primary'}
          onClick={methods.handleSubmit(onSubmit)}
          disabled={mutation.isPending}
          sx={{ position: 'relative', minWidth: '5rem' }}
        >
          {!mutation.isPending && mode === 'Delete' && <span>Delete</span>}
          {!mutation.isPending && mode !== 'Delete' && (
            <span>{mode === 'Edit' ? 'Save' : 'Add'}</span>
          )}
          {mutation.isPending && (
            <CircularProgress color="secondary" size={20} />
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductEditDialog;
